/**
 * SurveyLandingPage - Neutral civic landing page for zapojsedovoleb.cz
 *
 * Shown when visiting the root path on the survey-only domain.
 * No Phoenix branding, Czech language only, civic engagement feel.
 * Lists all active public surveys with municipality search/filter.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { listPublicSurveys } from '../../services/api';
import { SurveyListItem } from '../../types';

const ELECTION_TYPE_LABELS: Record<string, string> = {
  komunalni: 'Komunální',
  senatni: 'Senátní',
};

const ELECTION_TYPE_COLORS: Record<string, string> = {
  komunalni: 'bg-blue-100 text-blue-800',
  senatni: 'bg-red-100 text-red-800',
};

// ============================================================================
// SurveyCard
// ============================================================================

interface SurveyCardProps {
  survey: SurveyListItem;
  onSelect: (id: string) => void;
}

const SurveyCard: React.FC<SurveyCardProps> = ({ survey, onSelect }) => {
  const badgeClass =
    ELECTION_TYPE_COLORS[survey.election_type] ?? 'bg-gray-100 text-gray-700';
  const badgeLabel =
    ELECTION_TYPE_LABELS[survey.election_type] ?? survey.election_type;

  return (
    <div
      className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer flex flex-col gap-3"
      onClick={() => onSelect(survey.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(survey.id); }}
      aria-label={`Vyplnit dotazník pro ${survey.municipality_name}`}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900 leading-snug flex-1">
          {survey.municipality_name}
        </h3>
        <span className={`text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${badgeClass}`}>
          {badgeLabel}
        </span>
      </div>

      {survey.title && (
        <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">
          {survey.title}
        </p>
      )}

      <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-100">
        <span className="text-xs text-gray-400">
          {survey.response_count === 0
            ? 'Zatím bez odpovědí'
            : `${survey.response_count} ${survey.response_count === 1 ? 'odpověď' : survey.response_count < 5 ? 'odpovědi' : 'odpovědí'}`}
        </span>
        <button
          className="text-sm font-medium text-blue-700 hover:text-blue-900 transition-colors"
          onClick={(e) => { e.stopPropagation(); onSelect(survey.id); }}
        >
          Vyplnit dotazník →
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// SurveyLandingPage
// ============================================================================

const SurveyLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<SurveyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    let cancelled = false;

    const fetchSurveys = async () => {
      try {
        const data = await listPublicSurveys();
        if (!cancelled) {
          setSurveys(data.surveys);
        }
      } catch (err: any) {
        if (!cancelled) {
          setFetchError(err.message || 'Nepodařilo se načíst dotazníky');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSurveys();
    return () => { cancelled = true; };
  }, []);

  const filteredSurveys = useMemo(() => {
    if (!filterText.trim()) return surveys;
    const lower = filterText.toLowerCase();
    return surveys.filter(
      (s) =>
        s.municipality_name.toLowerCase().includes(lower) ||
        s.title.toLowerCase().includes(lower)
    );
  }, [surveys, filterText]);

  const handleSurveySelect = (id: string) => {
    navigate(`/${id}`);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header stripe in Czech flag colors */}
      <div className="h-2 bg-gradient-to-r from-blue-700 via-white to-red-600" />

      {/* Hero section */}
      <main className="flex-1 px-4 py-16">
        <div className="max-w-2xl mx-auto text-center">

          {/* Icon */}
          <div className="w-20 h-20 bg-blue-700 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
            <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Zapoj se do voleb!
          </h1>

          {/* Subtitle */}
          <p className="text-xl text-blue-700 font-medium mb-6">
            Občanský průzkum názorů ve vaší obci
          </p>

          {/* Description */}
          <p className="text-gray-600 text-lg leading-relaxed mb-10 max-w-xl mx-auto">
            Anonymní dotazník vám zabere přibližně 5 minut. Vaše odpovědi
            pomohou politickým stranám a kandidátům lépe pochopit, co je
            pro obyvatele obce skutečně důležité.
          </p>

          {/* Feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
            <div className="bg-blue-50 rounded-2xl p-5">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <p className="font-semibold text-gray-900 text-sm">Zcela anonymní</p>
              <p className="text-gray-500 text-xs mt-1">Žádné osobní údaje, žádná registrace</p>
            </div>

            <div className="bg-blue-50 rounded-2xl p-5">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="font-semibold text-gray-900 text-sm">Zabere 5 minut</p>
              <p className="text-gray-500 text-xs mt-1">Rychlé a jednoduché otázky</p>
            </div>

            <div className="bg-blue-50 rounded-2xl p-5">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <p className="font-semibold text-gray-900 text-sm">Pro vaši obec</p>
              <p className="text-gray-500 text-xs mt-1">Výsledky jdou přímo kandidátům</p>
            </div>
          </div>
        </div>

        {/* ================================================================
            Survey listing section
            ================================================================ */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
            Dostupné dotazníky
          </h2>
          <p className="text-gray-500 text-sm text-center mb-6">
            Vyhledejte svou obec a vyplňte průzkum
          </p>

          {/* Search input */}
          <div className="relative mb-6">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 0 5 11a6 6 0 0 0 12 0z" />
            </svg>
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Hledat obec nebo název dotazníku…"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              aria-label="Hledat dotazník"
            />
          </div>

          {/* States */}
          {loading && (
            <div className="flex flex-col items-center gap-3 py-16 text-gray-400">
              <svg className="w-8 h-8 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <span className="text-sm">Načítám dostupné dotazníky…</span>
            </div>
          )}

          {!loading && fetchError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 text-center">
              Nepodařilo se načíst dotazníky. Zkuste obnovit stránku.
            </div>
          )}

          {!loading && !fetchError && filteredSurveys.length === 0 && filterText.trim() && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-sm text-gray-500 text-center">
              Žádné dotazníky pro tuto obec
            </div>
          )}

          {!loading && !fetchError && surveys.length === 0 && !filterText.trim() && (
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-left">
              <p className="text-gray-700 text-sm leading-relaxed">
                <span className="font-semibold">Jak se zapojit?</span> Odkaz na průzkum obdržíte od
                kandidátů nebo politické strany ve vaší obci — obvykle ve formě QR kódu na letáku
                nebo odkazu v e-mailu. Každý průzkum je specifický pro konkrétní obec a volby.
              </p>
            </div>
          )}

          {!loading && !fetchError && filteredSurveys.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredSurveys.map((survey) => (
                <SurveyCard
                  key={survey.id}
                  survey={survey}
                  onSelect={handleSurveySelect}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center border-t border-gray-100 mt-8">
        <p className="text-gray-400 text-sm">
          zapojsedovoleb.cz &mdash; Občanské průzkumy pro místní volby
        </p>
      </footer>

      {/* Bottom stripe */}
      <div className="h-2 bg-gradient-to-r from-blue-700 via-white to-red-600" />
    </div>
  );
};

export default SurveyLandingPage;
